import {
  Accordion,
  AccordionDetails,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemProps,
  ListItemText,
  Stack,
  Typography,
} from '@mui/material';
import {
  CopyShareUrl,
  DownloadShareLink,
  EmailShareLink,
  FacebookShareLink,
} from 'projects/sharedComponents/src/pet/shareUI/links/shareLinks';
import { FC, VFC } from 'react';
import { HeapAction, HeapCategory, HeapMetadata } from 'clientServerShared/types/eventTracking';

import { AccordionProps } from '@mui/material/Accordion/Accordion';
import AccordionSummary from '@mui/material/AccordionSummary';
import { CharityIcon } from 'projects/sharedComponents/src/icons/charityIcon';
import { CircularIconContainer } from 'projects/sharedComponents/src/icons/layoutComponents/circularIconContainer';
import { DownloadIcon } from 'projects/sharedComponents/src/icons/sharing/downloadIcon';
import { EmailIcon } from 'projects/sharedComponents/src/icons/sharing/emailIcon';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { FacebookIcon } from 'projects/sharedComponents/src/icons/sharing/facebookIcon';
import { LinkIcon } from 'projects/sharedComponents/src/icons/sharing/linkIcon';
import { ShareButtonFragment } from 'projects/sharedComponents/src/generated/graphql-client-types';
import { getHeapDataAttributes } from 'projects/sharedComponents/src/services/eventTracking/eventTracking';
import { styled } from '@mui/material/styles';

type ShareAccordionProps = {
  shareOptions?: ShareButtonFragment | null;
  listItemProps?: ListItemProps;
} & Pick<HeapMetadata, 'heapCategory'>;
export const ShareAccordion: VFC<ShareAccordionProps> = ({ shareOptions, heapCategory, listItemProps = {} }) => {
  const hasShareOptions = !!shareOptions;

  if (!hasShareOptions) {
    return null;
  }

  return (
    <StyledAccordion disableGutters elevation={0}>
      <AccordionSummary
        expandIcon={<ExpandMoreIcon sx={{ fontSize: 32 }} aria-hidden="true" htmlColor="black" />}
        {...getHeapDataAttributes({
          heapDetails: HeapCategory.SHARE_ACCORDION,
          heapAction: HeapAction.TOGGLE_ACCORDION,
          heapCategory,
        })}
      >
        <Stack spacing={{ xs: 2, sm: 3 }} direction="row" alignItems="center">
          <ShareIconContainer>
            <CharityIcon sx={{ fontSize: 32 }} />
          </ShareIconContainer>
          <Typography component="h4" fontWeight={700}>
            Share Results
          </Typography>
        </Stack>
      </AccordionSummary>
      <AccordionDetails>
        <List disablePadding>
          <ListItem disablePadding {...listItemProps}>
            <ListItemButton
              component={FacebookShareLink}
              heapMetadata={{ heapCategory }}
              variant="embarkLinkBlue"
              href={shareOptions.facebookUrl}
            >
              <ListItemIcon>
                <ShareIconContainer>
                  <FacebookIcon aria-hidden="true" sx={{ fontSize: 28 }} />
                </ShareIconContainer>
              </ListItemIcon>
              <ListItemText
                primary="Share results on Facebook"
                primaryTypographyProps={{
                  variant: 'body1',
                  fontWeight: 'bold',
                }}
              />
            </ListItemButton>
          </ListItem>
          <ListItem disablePadding {...listItemProps}>
            <ListItemButton
              component={EmailShareLink}
              heapMetadata={{ heapCategory }}
              variant="embarkLinkBlue"
              href={shareOptions.emailMailTo}
            >
              <ListItemIcon>
                <ShareIconContainer>
                  <EmailIcon aria-hidden="true" sx={{ fontSize: 28 }} />
                </ShareIconContainer>
              </ListItemIcon>
              <ListItemText
                primary="Email results"
                primaryTypographyProps={{
                  variant: 'body1',
                  fontWeight: 'bold',
                }}
              />
            </ListItemButton>
          </ListItem>
          <ListItem disablePadding {...listItemProps}>
            <ListItemButton
              component={DownloadShareLink}
              heapMetadata={{ heapCategory }}
              variant="embarkLinkBlue"
              href={shareOptions.downloadUrl ?? ''}
              shareItemId={shareOptions.shareItemId}
            >
              <ListItemIcon>
                <ShareIconContainer>
                  <DownloadIcon aria-hidden="true" sx={{ fontSize: 28 }} />
                </ShareIconContainer>
              </ListItemIcon>
              <ListItemText
                primary="Download results"
                primaryTypographyProps={{
                  variant: 'body1',
                  fontWeight: 'bold',
                }}
              />
            </ListItemButton>
          </ListItem>
          <ListItem disablePadding {...listItemProps}>
            <ListItemButton
              component={CopyShareUrl}
              heapMetadata={{ heapCategory }}
              variant="embarkLinkBlue"
              href={shareOptions.copyLinkUrl}
            >
              <ListItemIcon>
                <ShareIconContainer>
                  <LinkIcon aria-hidden="true" sx={{ fontSize: 28 }} />
                </ShareIconContainer>
              </ListItemIcon>
              <ListItemText
                primary="Copy link to public profile"
                primaryTypographyProps={{
                  variant: 'body1',
                  fontWeight: 'bold',
                }}
              />
            </ListItemButton>
          </ListItem>
        </List>
      </AccordionDetails>
    </StyledAccordion>
  );
};

const ShareIconContainer: FC = ({ children }) => (
  <CircularIconContainer
    shapeContainerProps={{
      alignItems: 'center',
      sx: { bgcolor: (theme) => theme.palette.helix.greyLightest, height: 56, width: 56 },
    }}
  >
    {children}
  </CircularIconContainer>
);

const StyledAccordion = styled(Accordion)<AccordionProps>(({ theme }) => ({
  'width': '100%',
  'backgroundColor': theme.palette.helix.background,
  '&:before': {
    opacity: 0,
  },
  '&, &:first-of-type': {
    borderRadius: theme.spacing(1),
  },
  '& .MuiAccordionSummary-root': {
    paddingRight: theme.spacing(1),
    paddingLeft: theme.spacing(2),
  },
  '& .MuiAccordionDetails-root': {
    paddingRight: 0,
    paddingLeft: 0,
  },
  '&.Mui-expanded, &.Mui-expanded .MuiAccordionDetails-root': {
    paddingBottom: 0,
  },
  [theme.breakpoints.up('sm')]: {
    '& .MuiAccordionSummary-root': {
      paddingRight: theme.spacing(2),
      paddingLeft: theme.spacing(3),
    },
  },
}));
